import {t, Trans} from '@lingui/macro'
import React from 'react'
import styled from 'styled-components'

import {Button, Callout, Link, LoadingOverlay, Stack, Text, tokens} from '@pleo-io/telescope'

import {ExternalProvider, useOauthUrl} from '@product-web/auth--oauth/context'
import {checkEmail, requestOtp} from '@product-web/auth--otp/api'
import config from '@product-web/config'
import {getErrorMessage} from '@product-web/error/message'
import {reportError} from '@product-web/error/report'
import {useAppLanguage} from '@product-web/i18n'
import googleLogo from '@product-web/images/google-logo.svg'
import microsoftLogo from '@product-web/images/microsoft-logo.svg'
import {PleoLogo} from '@product-web/telescope-lab/logo-pleo/logo-pleo'
import {useToaster} from '@product-web/toaster'
import {useDebouncePromise} from '@product-web/use-debounce-promise'
import {LoginScreen, useLoginLocationState} from '@product-web-features/sca/login/login.helpers'

import {EmailPhoto} from './email-photo'

import {DEBOUNCE_OTP_REQUEST_MS} from '../../../libs/debounce-otp-request'
import * as s from '../../../libs/otp.styles'
import {useEmailInput} from '../use-email-input'

const commercialWebUrl = config.endpoints.commercial

export function EmailScreen() {
    const {state: locationState, update, clear} = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [accountLockedTimeout, setAccountLockedTimeout] = React.useState(0)

    const {showToast} = useToaster()
    const appLanguage = useAppLanguage()
    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    React.useEffect(() => {
        // We want to show account locked timeout callout when necessary, but
        // we should hide it on any state update or even on page refresh
        // thus we have to immediately clean this state property and instead store it
        // in the local component's state

        if (locationState.accountLockedTimeout) {
            setAccountLockedTimeout(locationState.accountLockedTimeout)
        }

        update(
            {
                ...locationState,
                // always clean up account locked state
                accountLockedTimeout: undefined,
            },
            true,
        )
    }, [])

    const navigateToNextStep = ({email, ssoAuthUrl}: {email: string; ssoAuthUrl?: string}) => {
        if (ssoAuthUrl) {
            window.location.href = ssoAuthUrl
        } else {
            update({
                email,
                screen: LoginScreen.PASSCODE,
            })
        }
    }

    // If SSO with google we navigate to the validation screen
    React.useEffect(() => {
        const email = locationState?.email
        const loginToken = locationState?.loginToken
        if (!loginToken || !email || isLoading || hasError) {
            return
        }

        const requestOtpAndNavigateToNextStep = async () => {
            try {
                // We set this isLoading state to avoid requesting the otp more than once.
                // There's no need to update the isLoading state after this request is resolved since the user is already on the next screen
                setIsLoading(true)
                const response = await requestOtpDebounced({
                    email,
                    loginToken,
                    language: appLanguage,
                })
                update({
                    ...response,
                    email: locationState.email,
                    screen: LoginScreen.OTP_EMAIL_VALIDATION,
                })
            } catch (error) {
                const message = getErrorMessage(error)
                setHasError(!!message)
                setIsLoading(false)
                update({
                    email: null,
                    screen: LoginScreen.EMAIL,
                })
                reportError(error)
            }
        }

        requestOtpAndNavigateToNextStep()
    }, [locationState.loginToken, locationState.email, update, isLoading, hasError, clear])

    const onSubmitEmail = async (email: string) => {
        setIsLoading(true)
        try {
            const response = await checkEmail(email)
            setIsLoading(false)
            if (response.legacy) {
                setIsLoading(false)
                setEmailDoesNotExists()
            } else {
                navigateToNextStep({...response, email})
            }
        } catch (error) {
            setIsLoading(false)
            update({email: null})
            reset()
            showToast(t`Please try again`, {
                title: t`Something went wrong.`,
                level: 'error',
            })
            reportError(error)
        }
    }

    const {element, setEmailDoesNotExists, reset} = useEmailInput({
        isLoading: Boolean(locationState.email && isLoading),
        initialValue: locationState.email,
        onSubmitEmail,
        inputLabel: t`Email (including SAML SSO)`,
        buttonLabel: t`Continue`,
    })

    const googleUrl = useOauthUrl({
        action: 'external',
        provider: ExternalProvider.GOOGLE,
        scope: ['resource:basic'],
        register: false,
    })

    const microsoftUrl = useOauthUrl({
        action: 'external',
        provider: ExternalProvider.MICROSOFT,
        scope: ['resource:basic'],
        register: false,
    })

    if (!googleUrl) {
        return null
    }

    if (locationState.loginToken && !hasError) {
        return <LoadingOverlay data-testid="loading-indicator" />
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo>
                    <PleoLogo />
                </s.Logo>
                <s.Form>
                    <section>
                        <Stack space={18} stretch>
                            <Stack space={32} stretch>
                                <Text variant="3xlarge-accent">
                                    <Trans>Log in to Pleo</Trans>
                                </Text>
                                <Stack space={20} stretch>
                                    <OauthButton href={googleUrl}>
                                        <img src={googleLogo} alt={t`Google logo`} />
                                        <Trans>Continue with Google</Trans>
                                    </OauthButton>
                                    <OauthButton href={microsoftUrl}>
                                        <img src={microsoftLogo} alt={t`Microsoft logo`} />
                                        <Trans>Continue with Microsoft</Trans>
                                    </OauthButton>
                                </Stack>
                                <DividerWrapper>
                                    <Divider>
                                        <Trans>or</Trans>
                                    </Divider>
                                </DividerWrapper>
                            </Stack>
                            <Stack stretch space={40}>
                                {accountLockedTimeout > 0 && (
                                    <Callout variant="negative">
                                        <Callout.Text>
                                            <Trans>
                                                Too many verification code requests. Your account
                                                has been locked for 24 hours for security reasons.
                                                Please contact support@pleo.io to get access to your
                                                account.
                                            </Trans>
                                        </Callout.Text>
                                    </Callout>
                                )}
                                {element}
                            </Stack>
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Trans>
                        No account? <Link href={commercialWebUrl}>Get started for free</Link>
                    </Trans>
                </s.BottomNav>
            </s.Content>
            <EmailPhoto />
        </s.Wrapper>
    )
}

const OauthButton = styled(Button).attrs({variant: 'secondary'})`
    img {
        display: inline-block;
        margin-right: ${tokens.spacing8};
        margin-left: ${tokens.spacing8};
    }
`

export const DividerWrapper = styled.div`
    margin: ${tokens.spacing20} 0;
`

export const Divider = styled(Text).attrs({variant: 'medium-default', as: 'p'})`
    width: 100%;
    display: flex;

    &::before,
    &::after {
        content: '';
        border-bottom: ${tokens.borderPrimary};
        margin: auto;
    }

    &::before {
        flex: 1;
        margin-right: ${tokens.spacing12};
    }

    &::after {
        flex: 7;
        margin-left: ${tokens.spacing12};
    }
`
